import qs from 'qs'

import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'

export interface ISignupQueryParams {
  focusedPlatform?: PlatformEnum
  subscriptionPlan?: SubscriptionPlanEnum
  /** Passed on as given: saving it is where anything but monthly or annual is dropped. */
  subscriptionPeriod?: string
  affiliateCode?: string
  /** Sign-in owns `redirect` and `forgotpassword`; this page only forwards the query string. */
  handOffToSignin: boolean
}

const FOCUSABLE_PLATFORMS = [PlatformEnum.Slack, PlatformEnum.Microsoft, PlatformEnum.Google, PlatformEnum.Email]

const STORABLE_PLANS = [SubscriptionPlanEnum.core, SubscriptionPlanEnum.complete]

/**
 * Freemium is no longer offered, so its links (the old `Free` ones included) become Complete,
 * as on the old page. Stored as Freemium, the later steps would promise a Freemium subscription
 * while the company is created on Complete.
 */
const RETIRED_PLANS = ['Free', SubscriptionPlanEnum.freemium]

const readString = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined)

const readPlan = (value: unknown): SubscriptionPlanEnum | undefined => {
  const plan = readString(value)
  if (plan && RETIRED_PLANS.includes(plan)) return SubscriptionPlanEnum.complete
  return STORABLE_PLANS.includes(plan as SubscriptionPlanEnum) ? (plan as SubscriptionPlanEnum) : undefined
}

const readPlatform = (value: unknown): PlatformEnum | undefined => {
  const platform = readString(value)
  return FOCUSABLE_PLATFORMS.includes(platform as PlatformEnum) ? (platform as PlatformEnum) : undefined
}

/**
 * Parsed once from the URL so the page can derive what it needs while rendering rather than
 * copying the URL into state from an effect.
 */
export const parseSignupQueryParams = (search: string): ISignupQueryParams => {
  const parsed = qs.parse(search, { ignoreQueryPrefix: true })

  return {
    focusedPlatform: readPlatform(parsed.platform),
    subscriptionPlan: readPlan(parsed.subscriptionPlan),
    subscriptionPeriod: readString(parsed.subscriptionPeriod),
    affiliateCode: readString(parsed.affiliateCode),
    handOffToSignin: Boolean(readString(parsed.redirect) || readString(parsed.forgotpassword)),
  }
}
